import i18n from 'i18n-literally'

import { BiometricAuth, BiometryType, type BiometricAuthPlugin } from '@aparajita/capacitor-biometric-auth'
import { KeychainAccess, SecureStorage, type SecureStoragePlugin } from '@aparajita/capacitor-secure-storage'

import { isNativePlatform } from './platform'
import { type NativeAuthTokens, parseNativeAuthTokens } from './tokens'

const AUTH_TOKENS_KEY = 'auth.tokens'
const AUTH_USER_KEY = 'auth.user'

export interface BiometricStatus {
  readonly available: boolean
  readonly enabled: boolean
  readonly label: string
}

type BiometricAuthApi = Pick<BiometricAuthPlugin, 'addResumeListener' | 'authenticate' | 'checkBiometry'>
type SecureStorageApi = Pick<
  SecureStoragePlugin,
  'get' | 'keys' | 'remove' | 'set' | 'setDefaultKeychainAccess' | 'setKeyPrefix' | 'setSynchronize'
>

interface NativeBiometricDependencies {
  readonly biometricAuth?: BiometricAuthApi
  readonly native?: boolean
  readonly secureStorage?: SecureStorageApi
}

const getBiometricLabel = (type: BiometryType): string => {
  if (type === BiometryType.faceId) return i18n`Face ID`
  if (type === BiometryType.touchId) return i18n`Touch ID`
  if (type === BiometryType.fingerprintAuthentication) return i18n`Fingerprint`
  if (type === BiometryType.faceAuthentication) return i18n`Face authentication`
  if (type === BiometryType.irisAuthentication) return i18n`Iris authentication`
  return i18n`Biometric authentication`
}

const getDependencies = ({
  biometricAuth = BiometricAuth,
  secureStorage = SecureStorage,
}: NativeBiometricDependencies = {}): { biometricAuth: BiometricAuthApi; secureStorage: SecureStorageApi } => ({
  biometricAuth,
  secureStorage,
})

const requireNative = (native: boolean): void => {
  if (!native) throw new Error('Biometric authentication is only available in the native AROYA app.')
}

const configureSecureStorage = async (storage: SecureStorageApi): Promise<void> => {
  await storage.setSynchronize(false)
  await storage.setKeyPrefix('aroya_')
  await storage.setDefaultKeychainAccess(KeychainAccess.whenPasscodeSetThisDeviceOnly)
}

export const checkNativeBiometrics = async ({
  native = isNativePlatform(),
  ...dependencyOptions
}: NativeBiometricDependencies = {}): Promise<BiometricStatus> => {
  if (!native) return Object.freeze({ available: false, enabled: false, label: '' })

  const { biometricAuth, secureStorage } = getDependencies(dependencyOptions)
  const result = await biometricAuth.checkBiometry()
  const enabled = result.isAvailable && (await hasNativeAuthTokens({ native, secureStorage }))
  return Object.freeze({
    available: result.isAvailable,
    enabled,
    label: getBiometricLabel(result.biometryType),
  })
}

export const authenticateNativeBiometrics = async ({
  native = isNativePlatform(),
  ...dependencyOptions
}: NativeBiometricDependencies = {}): Promise<void> => {
  requireNative(native)
  const { biometricAuth } = getDependencies(dependencyOptions)
  await biometricAuth.authenticate({
    allowDeviceCredential: false,
    androidSubtitle: i18n`Use biometric authentication to continue.`,
    androidTitle: i18n`Sign in to AROYA`,
    iosFallbackTitle: '',
    reason: i18n`Authenticate to sign in to AROYA.`,
  })
}

export const addNativeBiometricResumeListener = async (
  listener: () => unknown,
  { native = isNativePlatform(), biometricAuth }: Pick<NativeBiometricDependencies, 'biometricAuth' | 'native'> = {},
): Promise<() => Promise<void>> => {
  requireNative(native)
  if (typeof listener !== 'function') throw new TypeError('Biometric resume listener must be a function.')
  const { biometricAuth: auth } = getDependencies({ biometricAuth })
  await auth.checkBiometry()
  const handle = await auth.addResumeListener(() => {
    void listener()
  })
  return () => handle.remove()
}

export const hasNativeAuthTokens = async ({
  native = isNativePlatform(),
  secureStorage,
}: Pick<NativeBiometricDependencies, 'native' | 'secureStorage'> = {}): Promise<boolean> => {
  if (!native) return false
  const { secureStorage: storage } = getDependencies({ secureStorage })
  await configureSecureStorage(storage)
  return (await storage.keys(false)).includes(AUTH_TOKENS_KEY)
}

export const readNativeAuthTokens = async ({
  native = isNativePlatform(),
  secureStorage,
}: Pick<NativeBiometricDependencies, 'native' | 'secureStorage'> = {}): Promise<NativeAuthTokens | null> => {
  requireNative(native)
  const { secureStorage: storage } = getDependencies({ secureStorage })
  await configureSecureStorage(storage)
  return parseNativeAuthTokens(await storage.get(AUTH_TOKENS_KEY, false, false))
}

export const writeNativeAuthTokens = async (
  tokens: unknown,
  { native = isNativePlatform(), secureStorage }: Pick<NativeBiometricDependencies, 'native' | 'secureStorage'> = {},
): Promise<void> => {
  requireNative(native)
  const parsedTokens = parseNativeAuthTokens(tokens)
  if (!parsedTokens) throw new TypeError('Invalid authentication tokens.')
  const { secureStorage: storage } = getDependencies({ secureStorage })
  await configureSecureStorage(storage)
  await storage.set(AUTH_TOKENS_KEY, parsedTokens, false, false, KeychainAccess.whenPasscodeSetThisDeviceOnly)
  if (
    typeof parsedTokens.user === 'string' ||
    typeof parsedTokens.user === 'number' ||
    typeof parsedTokens.user === 'boolean'
  ) {
    await storage.set(AUTH_USER_KEY, parsedTokens.user, false, false, KeychainAccess.whenPasscodeSetThisDeviceOnly)
  } else {
    await storage.remove(AUTH_USER_KEY, false)
  }
}

export const readNativeAuthUser = async ({
  native = isNativePlatform(),
  secureStorage,
}: Pick<NativeBiometricDependencies, 'native' | 'secureStorage'> = {}): Promise<unknown> => {
  requireNative(native)
  const { secureStorage: storage } = getDependencies({ secureStorage })
  await configureSecureStorage(storage)
  return storage.get(AUTH_USER_KEY, false, false)
}

export const removeNativeAuthTokens = async ({
  native = isNativePlatform(),
  secureStorage,
}: Pick<NativeBiometricDependencies, 'native' | 'secureStorage'> = {}): Promise<void> => {
  requireNative(native)
  const { secureStorage: storage } = getDependencies({ secureStorage })
  await configureSecureStorage(storage)
  await storage.remove(AUTH_TOKENS_KEY, false)
  await storage.remove(AUTH_USER_KEY, false)
}
